import React, { useState, useEffect, useCallback, useRef } from 'react';
import DesignModal from './DesignModal';
import ProgressiveImage from './ProgressiveImage';
import MoodBoard from './MoodBoard';
import TopPicksLeaderboard from './TopPicksLeaderboard';
import TagCloud from './TagCloud';
import './MoodBoard.css';
import './BookmarksPanel.css';
import { API_URL as apiUrl } from '../config';
const PAGE_SIZE = 12;

// Debounce hook
function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// IntersectionObserver hook for infinite scroll sentinel
function useInfiniteScroll(callback, enabled) {
  const sentinelRef = useRef(null);
  useEffect(() => {
    if (!enabled) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) callback();
      },
      { rootMargin: '200px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [callback, enabled]);
  return sentinelRef;
}

// Stable browser fingerprint (no login needed)
function getFingerprint() {
  let fp = localStorage.getItem('fashionai_fp');
  if (!fp) {
    fp = Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem('fashionai_fp', fp);
  }
  return fp;
}

const fallbackItems = [
  { id: 'f1', prompt: 'Cyberpunk cat with neon lights', image_url: 'https://picsum.photos/seed/neoncat/400/400', likes: 0, user_liked: 0 },
  { id: 'f2', prompt: 'Cosmic nebula swirl', image_url: 'https://picsum.photos/seed/galaxy99/400/400', likes: 0, user_liked: 0 },
  { id: 'f3', prompt: 'Abstract geometric art', image_url: 'https://picsum.photos/seed/abstract7/400/400', likes: 0, user_liked: 0 },
  { id: 'f4', prompt: 'Dragon made of galaxies', image_url: 'https://picsum.photos/seed/dragon42/400/400', likes: 0, user_liked: 0 },
  { id: 'f5', prompt: 'Neon Tokyo street at night', image_url: 'https://picsum.photos/seed/tokyo88/400/400', likes: 0, user_liked: 0 },
  { id: 'f6', prompt: 'Minimalist mountain zen', image_url: 'https://picsum.photos/seed/zen11/400/400', likes: 0, user_liked: 0 },
];

function Gallery({ onUsePrompt, onToggleBookmark, isBookmarked: checkBookmarked }) {
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [sort, setSort] = useState('recent'); // 'recent' | 'likes'
  const [fingerprint] = useState(getFingerprint);
  const [usingFallback, setUsingFallback] = useState(false);
  const [stats, setStats] = useState(null);
  const [modalDesign, setModalDesign] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  // Recently viewed — track last 5 clicked gallery designs in localStorage
  const [recentlyViewed, setRecentlyViewed] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('fashionai_recently_viewed') || '[]');
    } catch { return []; }
  });

  const addToRecentlyViewed = (item) => {
    setRecentlyViewed(prev => {
      const filtered = prev.filter(d => d.id !== item.id);
      const next = [
        { id: item.id, image_url: item.image_url, prompt: item.prompt },
        ...filtered,
      ].slice(0, 5);
      try { localStorage.setItem('fashionai_recently_viewed', JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const [layoutMode, setLayoutMode] = useState(() =>
    localStorage.getItem('fashionai_layout') || 'grid'
  ); // 'grid' | 'masonry'
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 250);
  const [tagFilter, setTagFilter] = useState(null); // active tag cloud word

  // Multi-select / mood board state
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showMoodBoard, setShowMoodBoard] = useState(false);

  // Client-side filter by search query OR tag cloud word
  const activeFilter = tagFilter || debouncedSearch.trim();
  const filteredDesigns = activeFilter
    ? designs.filter(d => d.prompt && d.prompt.toLowerCase().includes(activeFilter.toLowerCase()))
    : designs;

  const handleTagSelect = (word) => {
    setTagFilter(word);
    if (word) setSearchQuery(''); // clear search when a tag is picked
  };

  const toggleLayout = () => {
    setLayoutMode(prev => {
      const next = prev === 'grid' ? 'masonry' : 'grid';
      localStorage.setItem('fashionai_layout', next);
      return next;
    });
  };

  const fetchDesigns = useCallback((newSort, reset = true) => {
    const currentOffset = reset ? 0 : offset;
    if (reset) {
      setLoading(true);
      setDesigns([]);
      setOffset(0);
      setHasMore(true);
    } else {
      setLoadingMore(true);
    }

    fetch(`${apiUrl}/designs?limit=${PAGE_SIZE}&offset=${currentOffset}&sort=${newSort === 'likes' ? 'likes' : 'recent'}&fingerprint=${fingerprint}`)
      .then(r => r.json())
      .then(data => {
        const valid = (data.designs || []).filter(d => d.image_url && d.image_url !== 'data:pending');
        if (reset) {
          if (valid.length > 0) {
            setDesigns(valid);
            setUsingFallback(false);
          } else {
            setDesigns(fallbackItems);
            setUsingFallback(true);
          }
        } else {
          setDesigns(prev => [...prev, ...valid]);
        }
        // If we got fewer than PAGE_SIZE results, no more pages
        setHasMore(valid.length === PAGE_SIZE);
        setOffset(currentOffset + valid.length);
      })
      .catch(() => {
        if (reset) {
          setDesigns(fallbackItems);
          setUsingFallback(true);
        }
        setHasMore(false);
      })
      .finally(() => {
        setLoading(false);
        setLoadingMore(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort, fingerprint, offset]);

  // Fetch global stats (total designs + likes)
  useEffect(() => {
    fetch(`${apiUrl}/stats`)
      .then(r => r.json())
      .then(data => setStats(data))
      .catch(() => {});
  }, []);

  // Initial load + sort change
  useEffect(() => {
    fetchDesigns(sort, true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort, fingerprint]);

  const handleLoadMore = useCallback(() => {
    if (loadingMore || !hasMore || usingFallback) return;
    fetchDesigns(sort, false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingMore, hasMore, usingFallback, sort]);

  // IntersectionObserver sentinel ref for infinite scroll
  const sentinelRef = useInfiniteScroll(handleLoadMore, hasMore && !usingFallback && !loading);

  const handleLike = async (e, item) => {
    e.stopPropagation();
    if (typeof item.id === 'string') return; // fallback items, no likes

    // Optimistic update
    setDesigns(prev => prev.map(d => d.id === item.id
      ? { ...d, likes: d.user_liked ? d.likes - 1 : d.likes + 1, user_liked: d.user_liked ? 0 : 1 }
      : d
    ));

    try {
      await fetch(`${apiUrl}/designs/${item.id}/like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fingerprint }),
      });
    } catch {
      fetchDesigns(sort, true); // revert on error
    }
  };

  const handleOpenModal = (item) => {
    setModalDesign(item);
    addToRecentlyViewed(item);
  };

  const handleCopyPrompt = (e, item) => {
    e.stopPropagation();
    if (!item.prompt) return;
    navigator.clipboard.writeText(item.prompt).then(() => {
      setCopiedId(item.id);
      setTimeout(() => setCopiedId(null), 1500);
    }).catch(() => {
      // fallback for older browsers
      const ta = document.createElement('textarea');
      ta.value = item.prompt;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopiedId(item.id);
      setTimeout(() => setCopiedId(null), 1500);
    });
  };

  // Multi-select handlers
  const toggleSelectMode = () => {
    setSelectMode(prev => !prev);
    setSelectedIds(new Set());
  };

  const toggleSelectItem = (e, item) => {
    if (!selectMode) return;
    e.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(item.id)) next.delete(item.id);
      else next.add(item.id);
      return next;
    });
  };

  const selectedDesigns = filteredDesigns.filter(d => selectedIds.has(d.id));

  const handleModalLike = async (item) => {
    if (typeof item.id === 'string') return; // fallback items
    // Optimistic update in modal + grid
    setDesigns(prev => prev.map(d => d.id === item.id
      ? { ...d, likes: d.user_liked ? d.likes - 1 : d.likes + 1, user_liked: d.user_liked ? 0 : 1 }
      : d
    ));
    setModalDesign(prev => prev
      ? { ...prev, likes: prev.user_liked ? prev.likes - 1 : prev.likes + 1, user_liked: prev.user_liked ? 0 : 1 }
      : prev
    );
    try {
      await fetch(`${apiUrl}/designs/${item.id}/like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fingerprint }),
      });
    } catch {
      fetchDesigns(sort, true);
    }
  };

  return (
    <section className="gallery-section" id="gallery">
      <div className="gallery-header">
        <div>
          <h2 className="section-title">Community Designs</h2>
          <p className="section-subtitle">
            Click any design to view details and use its prompt.
            {stats && stats.total_designs > 0 && (
              <span className="gallery-stats">
                {' '}· <strong>{stats.total_designs.toLocaleString()}</strong> designs
                {' '}· <strong>{stats.total_likes.toLocaleString()}</strong> likes
              </span>
            )}
          </p>
        </div>

        {/* Search bar */}
        <div className="gallery-search-bar">
          <span className="gallery-search-icon">🔍</span>
          <input
            type="text"
            className="gallery-search-input"
            placeholder="Search designs by keyword…"
            value={searchQuery}
            onChange={e => { setSearchQuery(e.target.value); if (e.target.value) setTagFilter(null); }}
            aria-label="Search community designs"
          />
          {searchQuery && (
            <button
              className="gallery-search-clear"
              onClick={() => setSearchQuery('')}
              aria-label="Clear search"
            >✕</button>
          )}
        </div>
        {activeFilter && (
          <div className="gallery-search-count">
            {filteredDesigns.length === 0
              ? `No results matching "${activeFilter}"`
              : `${filteredDesigns.length} result${filteredDesigns.length === 1 ? '' : 's'} matching "${activeFilter}"`}
          </div>
        )}

        <div className="gallery-controls">
          <div className="gallery-sort">
            <button
              className={`sort-btn ${sort === 'recent' ? 'active' : ''}`}
              onClick={() => setSort('recent')}
            >🕐 Recent</button>
            <button
              className={`sort-btn ${sort === 'likes' ? 'active' : ''}`}
              onClick={() => setSort('likes')}
            >🔥 Top Liked</button>
          </div>
          {/* Select mode toggle */}
          <button
            className={`gallery-select-toggle ${selectMode ? 'active' : ''}`}
            onClick={toggleSelectMode}
            title={selectMode ? 'Exit select mode' : 'Select designs for mood board'}
          >
            {selectMode ? `✓ Selecting (${selectedIds.size})` : '☐ Select'}
          </button>

          <div className="gallery-layout-toggle" title={layoutMode === 'grid' ? 'Switch to Masonry' : 'Switch to Grid'}>
            <button
              className={`layout-btn ${layoutMode === 'grid' ? 'active' : ''}`}
              onClick={() => layoutMode !== 'grid' && toggleLayout()}
              aria-label="Grid layout"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <rect x="0" y="0" width="7" height="7" rx="1"/>
                <rect x="9" y="0" width="7" height="7" rx="1"/>
                <rect x="0" y="9" width="7" height="7" rx="1"/>
                <rect x="9" y="9" width="7" height="7" rx="1"/>
              </svg>
            </button>
            <button
              className={`layout-btn ${layoutMode === 'masonry' ? 'active' : ''}`}
              onClick={() => layoutMode !== 'masonry' && toggleLayout()}
              aria-label="Masonry layout"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <rect x="0" y="0" width="7" height="10" rx="1"/>
                <rect x="9" y="0" width="7" height="6" rx="1"/>
                <rect x="0" y="12" width="7" height="4" rx="1"/>
                <rect x="9" y="8" width="7" height="8" rx="1"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Recently Viewed strip */}
      {recentlyViewed.length > 0 && (
        <div className="recently-viewed-section">
          <div className="recently-viewed-header">
            <span className="recently-viewed-label">🕐 Recently Viewed</span>
            <button
              className="recently-viewed-clear"
              onClick={() => {
                setRecentlyViewed([]);
                try { localStorage.removeItem('fashionai_recently_viewed'); } catch {}
              }}
              title="Clear history"
            >✕ Clear</button>
          </div>
          <div className="recently-viewed-strip">
            {recentlyViewed.map(item => (
              <div
                key={item.id}
                className="recently-viewed-tile"
                onClick={() => handleOpenModal(item)}
                title={item.prompt}
              >
                <img
                  src={item.image_url}
                  alt={item.prompt}
                  className="recently-viewed-img"
                  loading="lazy"
                />
                <div className="recently-viewed-overlay">
                  <p className="recently-viewed-prompt">{item.prompt}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* This week's top picks leaderboard */}
      <TopPicksLeaderboard onUsePrompt={onUsePrompt} />

      {/* Tag cloud — shown once we have designs */}
      {!loading && !usingFallback && designs.length > 0 && (
        <TagCloud
          designs={designs}
          activeWord={tagFilter}
          onSelect={handleTagSelect}
        />
      )}

      {loading ? (
        <div className="gallery-loading">
          {[...Array(9)].map((_, i) => (
            <div
              key={i}
              className="gallery-skeleton skeleton"
              style={{ animationDelay: `${(i % 3) * 0.15}s` }}
            />
          ))}
        </div>
      ) : (
        <>
          <div className={`gallery-grid ${layoutMode === 'masonry' ? 'gallery-masonry' : ''} ${selectMode ? 'gallery-select-mode' : ''}`}>
            {filteredDesigns.map((item, index) => {
              const isSelected = selectedIds.has(item.id);
              return (
              <div
                key={item.id}
                className={`gallery-item ${layoutMode === 'masonry' ? 'gallery-masonry-item' : ''} ${selectMode && isSelected ? 'selected' : ''}`}
                onClick={selectMode ? (e) => toggleSelectItem(e, item) : () => handleOpenModal(item)}
                style={layoutMode === 'masonry' ? { '--masonry-span': (index % 5 === 0 ? 2 : 1) } : {}}
              >
                {selectMode && (
                  <div className="gallery-item-checkbox" />
                )}
                <ProgressiveImage src={item.image_url} alt={item.prompt} masonry={layoutMode === 'masonry'} />

                {/* Like button */}
                <button
                  className={`like-btn ${item.user_liked ? 'liked' : ''}`}
                  onClick={(e) => handleLike(e, item)}
                  title={item.user_liked ? 'Unlike' : 'Like'}
                >
                  {item.user_liked ? '❤️' : '🤍'} {item.likes > 0 ? item.likes : ''}
                </button>

                {/* Copy prompt button */}
                <button
                  className={`copy-prompt-btn ${copiedId === item.id ? 'copied' : ''}`}
                  onClick={(e) => handleCopyPrompt(e, item)}
                  title="Copy prompt"
                >
                  {copiedId === item.id ? '✅' : '📋'}
                </button>

                {/* Bookmark button */}
                {onToggleBookmark && (
                  <button
                    className={`gallery-card-bm-btn${checkBookmarked && checkBookmarked({ imageUrl: item.image_url, designId: item.id }) ? ' bookmarked' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleBookmark({ imageUrl: item.image_url, prompt: item.prompt, designId: item.id });
                    }}
                    title={checkBookmarked && checkBookmarked({ imageUrl: item.image_url, designId: item.id }) ? 'Remove bookmark' : 'Bookmark this design'}
                  >
                    🔖
                  </button>
                )}

                {/* Date badge */}
                {item.created_at && (
                  <div className="gallery-item-date">
                    {new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                )}

                {/* Hover overlay */}
                <div className="gallery-item-overlay">
                  <p>"{item.prompt}"</p>
                  <span className="gallery-use-btn">
                    {selectMode ? (selectedIds.has(item.id) ? '✓ Selected' : '☐ Select') : '🔍 View details'}
                  </span>
                </div>
              </div>
              );
            })}
          </div>

          {/* Infinite scroll sentinel */}
          {!usingFallback && (
            <div ref={sentinelRef} className="gallery-sentinel" aria-hidden="true">
              {loadingMore && (
                <div className="gallery-load-more">
                  <span className="load-more-spinner">
                    <span className="spinner-dot" />
                    <span className="spinner-dot" />
                    <span className="spinner-dot" />
                  </span>
                </div>
              )}
            </div>
          )}

          {/* End of results */}
          {!usingFallback && !hasMore && designs.length > PAGE_SIZE && (
            <div className="gallery-end-msg">✨ You've seen all {designs.length} designs</div>
          )}
        </>
      )}
      {modalDesign && !selectMode && (
        <DesignModal
          design={modalDesign}
          onClose={() => setModalDesign(null)}
          onUsePrompt={onUsePrompt}
          onLike={handleModalLike}
        />
      )}

      {/* Floating mood board action bar */}
      {selectMode && selectedIds.size > 0 && (
        <div className="moodboard-fab">
          <span className="moodboard-fab-count">{selectedIds.size} selected</span>
          <button
            className="moodboard-fab-btn"
            onClick={() => setShowMoodBoard(true)}
            disabled={selectedIds.size < 2}
            title={selectedIds.size < 2 ? 'Select at least 2 designs' : 'Create mood board'}
          >
            🎨 Create Mood Board
          </button>
          <button className="moodboard-fab-clear" onClick={() => setSelectedIds(new Set())}>
            Clear
          </button>
        </div>
      )}

      {/* MoodBoard modal */}
      {showMoodBoard && selectedDesigns.length >= 2 && (
        <MoodBoard
          designs={selectedDesigns}
          onClose={() => {
            setShowMoodBoard(false);
          }}
        />
      )}
    </section>
  );
}

export default Gallery;
